import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { Button } from '../controls/Button.js';

export interface EmptyStateProps {
  readonly title?: string;
  readonly body?: string;
  readonly actions?: ReactNode;
  readonly className?: string;
}

/**
 * Num produto sobre honestidade epistemologica, o vazio e conteudo:
 * "nao temos nada sobre isto" e uma resposta legitima e tem de ser dita.
 */
export function EmptyState({ title, body, actions, className }: EmptyStateProps) {
  const { t } = useTranslation();
  return (
    <div className={clsx('et-empty', className)}>
      <h4 className="et-empty__title">{title ?? t('state.emptyTitle')}</h4>
      <p className="et-empty__body">{body ?? t('state.emptyBody')}</p>
      {actions && <div className="et-empty__actions">{actions}</div>}
    </div>
  );
}

export interface SkeletonProps {
  readonly width?: string;
  readonly height?: string;
  readonly radius?: string;
  readonly className?: string;
}

export function Skeleton({ width = '100%', height = '12px', radius, className }: SkeletonProps) {
  const { t } = useTranslation();
  return (
    <span
      className={clsx('et-skeleton', className)}
      style={{ width, height, ...(radius ? { borderRadius: radius } : {}) }}
      role="status"
      aria-label={t('state.loading')}
    />
  );
}

export interface ErrorCardProps {
  readonly title?: string;
  readonly body?: string;
  readonly onRetry?: () => void;
  readonly className?: string;
}

/**
 * Quando as fontes nao carregam, a app diz explicitamente que a afirmacao nao
 * deve ser tratada como verificada. Mostrar o texto sem o aviso contradiria a
 * regra central do produto.
 */
export function ErrorCard({ title, body, onRetry, className }: ErrorCardProps) {
  const { t } = useTranslation();
  return (
    <div className={clsx('et-errorcard', className)} role="alert">
      <p className="et-errorcard__title">{title ?? t('state.errorTitle')}</p>
      <p className="et-errorcard__body">{body ?? t('state.errorBody')}</p>
      {onRetry && <Button variant="ghost" size="sm" onClick={onRetry}>{t('action.retry')}</Button>}
    </div>
  );
}
